/**
 * InjuryInsurance — Proactive Injury Risk Alerts (Injury Insurance rebuild,
 * Stage 2)
 *
 * Every number here is real: risk score/band/percentile come straight off
 * injuryRiskService.computeRisk — the EXACT primitive the Waiver Wire uses
 * (Directional Change 3), so a player's colour here can never disagree with
 * their colour there. history/currentRisk/jaiView are one validated Claude
 * call explaining those numbers, never inventing them (see
 * injuryInsuranceService.js). No hardcoded preview/mock data path — every
 * other rebuilt tool in this series (Trade Analyser, Waiver Wire, Player/
 * Team Analyser) dropped its IS_PREVIEW block once real data existed; this
 * one does the same.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import { addNote } from '../services/notesStore'
import AppShell from '../components/AppShell'
import ToolHeader from '../components/ToolHeader'
import JaiRichText from '../components/JaiRichText'
import PlayerPhoto from '../components/PlayerPhoto'
import TeamChip from '../components/TeamChip'
import StickyAskBar from '../components/StickyAskBar'
import UpgradePrompt from '../components/UpgradePrompt'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Card, PosChip, StatStrip, StatusPill, PrimaryBtn, solidChip, pctTone, RefreshButton, Notice } from '../components/JaiV2'
import { JAIMascot } from '../components/JAIMascot'

// Risk colour now comes from pctTone(riskScore, { invert: true }) (Aug 2026
// percentage-colouring pass) rather than this band lookup, so it shares the
// app-wide 80/50 banding instead of a bespoke red/orange/green cutoff. Kept
// only as the fallback color for a band with no numeric score.
const BAND_COLOR = { red: V2.error, orange: V2.warning, green: V2.success }

const ACTION_META = {
  waiver: { label: (name) => `Add ${name} on Waiver Wire →` },
  trade: { label: (name) => `Explore ${name} in Trade Analyser →` },
}

/** "Updated 2 hours ago" — same small helper OpponentScout.jsx's status line uses. */
function timeAgo(iso) {
  if (!iso) return null
  const ms = Date.now() - new Date(iso).getTime()
  const mins = Math.round(ms / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

// ── One player card ─────────────────────────────────────────────────────

function PlayerRiskCard({ row, sport, noteSaved, noteSaving, onSaveNote }) {
  const color = row.riskScore != null ? pctTone(row.riskScore, { invert: true }) : (BAND_COLOR[row.band] || V2.faint)
  const riskChip = solidChip(color)
  const actionMeta = row.action ? ACTION_META[row.action.kind] : null

  return (
    <V2Card pad="18px 20px" style={row.band === 'red' ? { border: '1px solid rgba(214,58,58,0.3)' } : {}}>
      {/* Photo, name, position/club chips */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <PlayerPhoto playerId={row.playerId} name={row.name} size={44} rounded="full" />
        <div style={{ minWidth: 180 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 15.5, fontWeight: 800, color: V2.navy }}>{row.name}</span>
            <StatusPill status={row.injuryStatus || 'ACTIVE'} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {row.position && <PosChip>{row.position}</PosChip>}
            <TeamChip abbr={row.team} sport={sport} />
          </div>
        </div>

        {/* Risk Index meter — colour now driven by pctTone(riskScore, { invert: true }) */}
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5, gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', ...riskChip, fontFamily: JAI_FONTS.mono, borderRadius: 5, padding: '2px 7px' }}>Risk index</span>
            <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              {row.percentile != null && (
                <span style={{ fontSize: 11, color: pctTone(row.percentile, { invert: true }), fontWeight: 700, fontFamily: JAI_FONTS.mono }}>
                  Riskier than {row.percentile}% of the league
                </span>
              )}
              <span style={{ fontSize: 13, fontWeight: 800, color, fontFamily: JAI_FONTS.mono }}>{row.riskScore}</span>
            </span>
          </div>
          <div style={{ height: 7, borderRadius: 999, background: 'rgba(10,31,68,0.06)', overflow: 'hidden' }}>
            <div style={{ width: `${row.riskScore}%`, height: '100%', borderRadius: 999, background: color }} />
          </div>
        </div>
      </div>

      {/* Injury history / current risk — two short labelled blocks */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 14, paddingTop: 13, borderTop: '1px solid rgba(10,31,68,0.05)' }}>
        <div>
          <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: V2.faint, fontFamily: JAI_FONTS.mono, marginBottom: 5 }}>
            Injury history
          </div>
          {row.history
            ? <JaiRichText content={row.history} style={{ fontSize: 13 }} />
            : <p style={{ fontSize: 13, color: V2.faint, margin: 0 }}>No history read available right now.</p>}
        </div>
        <div>
          <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: V2.faint, fontFamily: JAI_FONTS.mono, marginBottom: 5 }}>
            Current risk
          </div>
          {row.currentRisk
            ? <JaiRichText content={row.currentRisk} style={{ fontSize: 13 }} />
            : <p style={{ fontSize: 13, color: V2.faint, margin: 0 }}>No current read available right now.</p>}
        </div>
      </div>

      {/* What JAI thinks */}
      {row.jaiView && (
        <div style={{ marginTop: 14, paddingTop: 13, borderTop: '1px solid rgba(10,31,68,0.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <JAIMascot size={20} state="idle" animate={false} />
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: V2.navy }}>What JAI thinks</span>
          </div>
          <JaiRichText content={row.jaiView} style={{ fontSize: 14 }} />
        </div>
      )}

      {/* Action row — only when a real handcuff action exists. The handcuff
          is named in visible text on its own line, not hidden in a `title`
          tooltip (invisible on mobile, which is what the old page did). */}
      {row.action && actionMeta && (
        <div style={{ marginTop: 14, paddingTop: 13, borderTop: '1px solid rgba(10,31,68,0.05)' }}>
          {row.action.handcuff && (
            <p style={{ fontSize: 12.5, color: V2.mute, margin: '0 0 8px', lineHeight: 1.5 }}>
              {sport === 'nfl' ? 'Handcuff' : 'Pick Up'}: <strong style={{ color: V2.navy }}>{row.action.handcuff.name}</strong>
              {row.action.handcuff.ownershipPercent != null && (
                <> (<span style={{ color: pctTone(row.action.handcuff.ownershipPercent), fontWeight: 700 }}>{row.action.handcuff.ownershipPercent}% owned</span>)</>
              )}
              {row.action.handcuff.reason ? ` — ${row.action.handcuff.reason}` : ''}
            </p>
          )}
          <Link to={row.action.to} style={{ textDecoration: 'none' }}>
            <PrimaryBtn>{actionMeta.label(row.action.handcuff?.name || 'replacement')}</PrimaryBtn>
          </Link>
        </div>
      )}

      {/* Save to My Notes */}
      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={onSaveNote}
          disabled={noteSaved || noteSaving}
          style={{
            background: 'none', border: 'none', color: noteSaved ? V2.faint : V2.blue,
            fontWeight: 700, cursor: (noteSaved || noteSaving) ? 'default' : 'pointer', fontSize: 12, padding: 0,
            fontFamily: JAI_FONTS.tight,
          }}
        >
          {noteSaved ? 'Saved to My Notes' : noteSaving ? 'Saving…' : 'Save to My Notes →'}
        </button>
      </div>
    </V2Card>
  )
}

// ── Main ───────────────────────────────────────────────────────────────

export default function InjuryInsurance() {
  const { id } = useParams()

  const [league, setLeague] = useState(null)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [upgrade, setUpgrade] = useState(null)
  const [savedKeys, setSavedKeys] = useState(() => new Set())
  const [savingKeys, setSavingKeys] = useState(() => new Set())
  const [noteError, setNoteError] = useState('')

  const sport = (league?.sport || 'nba').toLowerCase()

  const load = useCallback(async ({ refresh = false } = {}) => {
    if (refresh) setRefreshing(true)
    setError('')
    setUpgrade(null)
    try {
      const res = await leaguesApi.getInjuryInsurance(id, { refresh })
      setData(res.data)
      setSavedKeys(new Set())
    } catch (err) {
      if (err.response?.data?.code === 'PLAN_UPGRADE_REQUIRED') {
        setUpgrade({ tool: err.response.data.requiredTool, message: err.response.data.error })
      } else {
        setError(err.response?.data?.error || "JAI couldn't load Injury Insurance right now. Try again in a minute.")
      }
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [id])

  useEffect(() => {
    let cancelled = false
    leaguesApi.get(id).then((r) => { if (!cancelled) setLeague(r.data.league) }).catch(() => {})
    load()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Riskiest first — the backend already sorts this way, but the page
  // doesn't rely on that being true (defensive, and self-documenting here).
  const players = useMemo(
    () => (data?.players ? [...data.players].sort((a, b) => b.riskScore - a.riskScore) : []),
    [data]
  )
  const hasPlayers = players.length > 0

  const rowKey = (row) => (row.playerId != null ? `id:${row.playerId}` : `name:${row.name}`)

  const saveNoteFor = async (row) => {
    const key = rowKey(row)
    if (savedKeys.has(key) || savingKeys.has(key)) return
    const text = `${row.name} — Injury Insurance (${row.band} risk, ${row.riskScore}/100)\n\n${row.history || ''}${row.currentRisk ? `\n\n${row.currentRisk}` : ''}${row.jaiView ? `\n\n${row.jaiView}` : ''}`.trim()
    setSavingKeys((prev) => new Set(prev).add(key))
    setNoteError('')
    try {
      await addNote(id, text, { source: 'injury-insurance', title: `Injury risk — ${row.name}` })
      setSavedKeys((prev) => new Set(prev).add(key))
    } catch {
      setNoteError(`Could not save ${row.name} to your notes. Try again.`)
    } finally {
      setSavingKeys((prev) => { const next = new Set(prev); next.delete(key); return next })
    }
  }

  const askBarContext = useMemo(() => {
    if (!data || !hasPlayers) return undefined
    const redOrange = players.filter((p) => p.band !== 'green')
    const playersText = redOrange
      .map((p) => `- ${p.name} (${p.position || '?'}, ${p.team || '?'}) — ${p.band} risk, score ${p.riskScore}. ${p.currentRisk || ''}`)
      .join('\n')
    return `THE USER IS LOOKING AT INJURY INSURANCE RIGHT NOW.\n` +
      `Team risk distribution: ${data.team.counts.red} red, ${data.team.counts.orange} orange, ${data.team.counts.green} green.\n` +
      `Red/orange players:\n${playersText || 'None — the whole roster is green.'}\n` +
      `JAI's headline: ${data.team.headline}`
  }, [data, hasPlayers, players])

  const gradeCell = data?.team?.injuryGrade ? [[data.team.injuryGrade, 'Injury Grade', V2.blue]] : []

  return (
    <AppShell>
      <div style={{ padding: '30px 40px', fontFamily: JAI_FONTS.tight }}>
        <div style={{ maxWidth: 880, margin: '0 auto', paddingBottom: 100 }}>
          <ToolHeader
            leagueName={league?.name}
            title="Injury Insurance"
            subtitle={data?.basis || (sport === 'nfl'
              ? 'JAI watches games missed, current status and injury news — and lines up the handcuff before you need it.'
              : 'JAI watches games missed, current status and injury news — and lines up the pick up before you need it.')}
            back={{ label: 'Go to League Home Screen', to: `/league/${id}` }}
          />

          {/* Team overview strip, top-centre — grade cell omitted entirely
              when null, never a placeholder letter (Directional Change 5). */}
          {hasPlayers && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 8 }}>
              <StatStrip cells={[
                ...gradeCell,
                [data.team.counts.red, 'Red', V2.error],
                [data.team.counts.orange, 'Orange', V2.warning],
                [data.team.counts.green, 'Green', V2.success],
              ]} />
            </div>
          )}

          {/* Headline + "Updated N hours ago" + refresh */}
          {hasPlayers && data.team.headline && (
            <div style={{ textAlign: 'center', marginBottom: 8, maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
              <JaiRichText content={data.team.headline} style={{ fontSize: 14 }} />
            </div>
          )}
          {hasPlayers && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginTop: 0, marginBottom: 24 }}>
              <p style={{ textAlign: 'center', fontSize: 11.5, color: V2.faint, margin: 0 }}>
                {timeAgo(data.asOf) ? `Updated ${timeAgo(data.asOf)}` : 'Just now'}
              </p>
              <RefreshButton onClick={() => load({ refresh: true })} loading={refreshing} label="Refresh now" credits="injuryInsurance" style={{ fontSize: 11.5, padding: '5px 12px' }} />
            </div>
          )}

          {upgrade && <UpgradePrompt tool={upgrade.tool} message={upgrade.message} />}

          {/* Error banner, with retry — one of the three explicit states */}
          {error && !upgrade && (
            <Notice tone="error" action="Try again" onAction={() => load()}>{error}</Notice>
          )}

          {/* Inline spinner while the first load is in flight — never a
              full-page loader; header/skeleton above render immediately. */}
          {loading && !error && !upgrade && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 0', gap: 12 }}>
              <JAIMascot size={56} state="thinking" animate={true} />
              <p style={{ fontSize: 13.5, color: V2.faint, margin: 0 }}>Reading your roster's injury risk…</p>
            </div>
          )}

          {/* Honest empty state — pre-draft or an unreadable-but-not-fatal
              roster; team.headline carries the real reason. Never a blank grid. */}
          {!loading && !error && data && !hasPlayers && (
            <V2Card pad="32px 24px" style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 13.5, color: 'rgba(10,31,68,0.72)', margin: 0 }}>
                {data.team?.headline || "JAI can't build an injury read for this roster right now."}
              </p>
            </V2Card>
          )}

          {/* Real content */}
          {!loading && !error && hasPlayers && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {noteError && (
                <p style={{ fontSize: 12.5, color: V2.error, margin: 0 }}>{noteError}</p>
              )}
              {players.map((row) => (
                <PlayerRiskCard
                  key={rowKey(row)}
                  row={row}
                  sport={sport}
                  noteSaved={savedKeys.has(rowKey(row))}
                  noteSaving={savingKeys.has(rowKey(row))}
                  onSaveNote={() => saveNoteFor(row)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <StickyAskBar
        leagueId={id}
        scope="injury"
        context={askBarContext}
        placeholder="Ask JAI about your injury risk"
      />
    </AppShell>
  )
}
